import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import configData from '../config.json';

// Theater mode: upcoming, orderable performances for a venue. Only fetched when
// the restaurant has TheaterModeEnabled — every other venue never hits the
// endpoint. Fails open to an empty list so a network hiccup can never crash the
// menu; the picker simply shows "no upcoming performances".
export const useTheaterPerformances = (restaurantId, enabled) => {
  const [performances, setPerformances] = useState([]);
  // When pre-ordering opens again, for the case the list is empty: the server
  // knows whether the booking window simply has not reached the next show yet
  // ("Opens 23/09") or the programme has run out entirely (null — promise the
  // guest nothing). Absent from older servers, which is why it defaults to
  // null rather than being required.
  const [opensDate, setOpensDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // The list is refetched from several triggers (mount, the review step, the
  // tab returning to the foreground). Two overlapping GETs could resolve out
  // of order and leave the older program on screen, so only one runs at a time.
  const inFlightRef = useRef(false);
  // Has a fetch actually finished? An empty list means two different things
  // before and after — "we don't know yet" and "this venue has nothing on
  // sale" — and the caller hides the pre-order tile on the second one only.
  const [hasFetched, setHasFetched] = useState(false);

  const fetchPerformances = useCallback(async () => {
    if (!enabled || !restaurantId) {
      setPerformances([]);
      setOpensDate(null);
      setHasFetched(false);
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      const response = await axios({
        method: 'GET',
        url: `${configData.SERVER_URL}/theater/performances/${restaurantId}`,
      });
      setPerformances(Array.isArray(response.data?.performances) ? response.data.performances : []);
      setOpensDate(response.data?.preorderOpensDate || null);
    } catch (error) {
      console.error('Error fetching theater performances:', error);
      setPerformances([]);
      setOpensDate(null);
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
      setHasFetched(true);
    }
  }, [restaurantId, enabled]);

  useEffect(() => {
    fetchPerformances();
  }, [fetchPerformances]);

  // A guest can sit on the menu for hours — phone pocketed between choosing a
  // dish and paying — while performances close behind them: the server drops a
  // performance OrderWindowMinutesAfterStart after curtain. Without this the
  // picker keeps offering a show that is already gone, and the guest only
  // finds out when the gate refuses the order at checkout. Refetching whenever
  // the tab comes back to the foreground keeps the program honest without
  // polling a venue that may have published a whole season.
  useEffect(() => {
    if (!enabled || !restaurantId) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchPerformances();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, restaurantId, fetchPerformances]);

  return {
    performances,
    opensDate,
    isLoading,
    hasFetched,
    fetchPerformances,
  };
};
